/**
 * DANDU Typography V1 — semantic roles for the controlled Class Pulse pilot.
 *
 * Keep this additive: do not change the global MUI theme while older screens
 * still contain local font weights/sizes. New screens may adopt these roles
 * incrementally after visual review.
 *
 * Inter faces actually loaded: 400, 500, 600, 700 (src/index.js).
 * Nunito is retained for family/child experiences, not applied in this pilot.
 */
export const danduFontFamilies = Object.freeze({
  teacher: 'Inter, Roboto, -apple-system, BlinkMacSystemFont, Segoe UI, Arial, sans-serif',
  family: 'Nunito, Inter, system-ui, sans-serif',
});

const readableSecondary = '#45556D';

export const teacherTypography = {
  pageTitle: {
    fontFamily: danduFontFamilies.teacher,
    fontWeight: 700, fontSize: { xs: 26, md: 30 },
    lineHeight: 1.25, letterSpacing: '-0.015em', color: '#17213c',
  },
  sectionTitle: {
    fontWeight: 700, fontSize: { xs: 18, md: 20 },
    lineHeight: 1.35, letterSpacing: '-0.012em', color: '#17213c',
  },
  cardTitle: {
    fontWeight: 600, fontSize: 15, lineHeight: 1.45, color: '#17213c',
  },
  studentName: {
    fontWeight: 600, fontSize: 14, lineHeight: 1.5, color: '#17213c',
    overflowWrap: 'anywhere',
  },
  kpiValue: {
    fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.012em',
    fontVariantNumeric: 'tabular-nums',
  },
  metricLabel: {
    fontSize: 13, lineHeight: 1.45, fontWeight: 500, color: readableSecondary,
  },
  metadata: {
    fontSize: 13, lineHeight: 1.5, fontWeight: 500, color: readableSecondary,
  },
  supportingText: {
    fontSize: 13, lineHeight: 1.6, fontWeight: 400, color: readableSecondary,
  },
  tableHeader: {
    fontSize: 13, lineHeight: 1.5, fontWeight: 600, color: '#46546A',
  },
  scoreUnit: {
    fontSize: { xs: 12, sm: 13 }, lineHeight: 1.45,
    fontWeight: 500, color: readableSecondary,
  },
  action: { fontWeight: 600, textTransform: 'none' },
  scorePill: {
    fontSize: 13, fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
  },
};

/**
 * SVG fonts are scaled by the containing SVG's viewBox. Larger source font
 * sizes on small screens keep the final visible type legible; every data
 * point remains available via the chart aria-label and results list.
 * Responsive rules are declared together to avoid CSS-order regressions.
 */
export const teacherChartText = {
  '& .dandu-chart-axis': {
    fontFamily: danduFontFamilies.teacher,
    fontSize: 12, fontWeight: 500, fill: readableSecondary,
    '@media (max-width: 599px)': { display: 'none' },
  },
  '& .dandu-chart-score': {
    fontFamily: danduFontFamilies.teacher,
    fontSize: 13, fontWeight: 700, fill: '#4232a1',
    '@media (max-width: 599px)': { fontSize: 30 },
  },
  '& .dandu-chart-date': {
    fontFamily: danduFontFamilies.teacher,
    fontSize: 12, fontWeight: 500, fill: readableSecondary,
    '@media (max-width: 599px)': { fontSize: 30 },
  },
  '& .dandu-chart-date-optional': {
    '@media (max-width: 599px)': { display: 'none' },
  },
};
